
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';

interface Question {
  id: string;
  skill: string;
  difficulty: string;
  question: string;
  option_a: string;
  option_b: string;
  option_c: string;
  option_d: string;
  correct_answer: string;
}

export const useQuiz = (skill: string, userId: string, onComplete: () => void) => {
  const TOTAL_QUESTIONS = 32; // 32 questions
  const QUIZ_TIME_LIMIT = 24 * 60; // 24 minutes in seconds
  const QUESTIONS_PER_DIFFICULTY = 8; // 8 questions per difficulty level
  const PASSING_SCORE = 90; // 90% passing score
  
  const { toast } = useToast();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<string, string>>({});
  const [timeLeft, setTimeLeft] = useState(QUIZ_TIME_LIMIT);
  const [totalTime] = useState(QUIZ_TIME_LIMIT);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [score, setScore] = useState(0);
  const [passed, setPassed] = useState(false);
  const [quizAttemptId, setQuizAttemptId] = useState<string | null>(null);

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        // First, fetch all questions for this skill to get the distribution
        const { data, error } = await supabase
          .from('quiz_config')
          .select('*')
          .eq('skill', skill);
        
        if (error) throw error;
        
        if (!data || data.length === 0) {
          setQuestions([]);
          setIsLoading(false);
          return;
        }

        // Group questions by difficulty
        const beginner = data.filter(q => q.difficulty === 'beginner' || q.difficulty === 'easy');
        const intermediate = data.filter(q => q.difficulty === 'intermediate');
        const advanced = data.filter(q => q.difficulty === 'advanced');
        const expert = data.filter(q => q.difficulty === 'expert');
        
        // Function to get random items from an array
        const getRandomItems = (arr: any[], count: number) => {
          const shuffled = [...arr].sort(() => 0.5 - Math.random());
          return shuffled.slice(0, count);
        };
        
        // Select questions from each difficulty level
        let selectedQuestions: Question[] = [];
        
        // Add beginner questions
        const beginnerCount = Math.min(beginner.length, QUESTIONS_PER_DIFFICULTY);
        selectedQuestions = [...selectedQuestions, ...getRandomItems(beginner, beginnerCount)];
        
        // Add intermediate questions
        const intermediateCount = Math.min(intermediate.length, QUESTIONS_PER_DIFFICULTY);
        selectedQuestions = [...selectedQuestions, ...getRandomItems(intermediate, intermediateCount)];
        
        // Add advanced questions
        const advancedCount = Math.min(advanced.length, QUESTIONS_PER_DIFFICULTY);
        selectedQuestions = [...selectedQuestions, ...getRandomItems(advanced, advancedCount)];
        
        // Add expert questions
        const expertCount = Math.min(expert.length, QUESTIONS_PER_DIFFICULTY);
        selectedQuestions = [...selectedQuestions, ...getRandomItems(expert, expertCount)];
        
        // If we still don't have enough questions, add random ones from any difficulty
        if (selectedQuestions.length < TOTAL_QUESTIONS) {
          const remainingNeeded = TOTAL_QUESTIONS - selectedQuestions.length;
          const existingIds = selectedQuestions.map(q => q.id);
          const remainingQuestions = data.filter(q => !existingIds.includes(q.id));
          selectedQuestions = [
            ...selectedQuestions,
            ...getRandomItems(remainingQuestions, remainingNeeded)
          ];
        }
        
        // Shuffle the final questions
        selectedQuestions.sort(() => 0.5 - Math.random());
        
        // Limit to TOTAL_QUESTIONS
        setQuestions(selectedQuestions.slice(0, TOTAL_QUESTIONS));
      } catch (error) {
        console.error('Error fetching questions:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchQuestions();

    // Create a quiz attempt when starting
    const createQuizAttempt = async () => {
      try {
        const { data, error } = await supabase
          .from('quiz_attempts')
          .insert({
            user_id: userId,
            skill: skill
          })
          .select('id')
          .single();
        
        if (error) throw error;
        
        if (data) {
          setQuizAttemptId(data.id);
        }
      } catch (error) {
        console.error('Error creating quiz attempt:', error);
      }
    };

    createQuizAttempt();

    // Set up timer
    const timer = setInterval(() => {
      setTimeLeft(prevTime => {
        if (prevTime <= 1) {
          clearInterval(timer);
          handleQuizEnd();
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [skill, userId]);

  const handleAnswerSelect = (questionId: string, answer: string) => {
    setSelectedAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePrevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const calculateScore = () => {
    let correctCount = 0;
    questions.forEach(question => {
      if (selectedAnswers[question.id] === question.correct_answer) {
        correctCount++;
      }
    });
    
    // Return a score out of 100
    return Math.round((correctCount / questions.length) * 100);
  };

  const saveUserResponses = async () => {
    if (!quizAttemptId) {
      console.error("Quiz attempt ID not set, cannot save responses");
      return;
    }
    
    try {
      // Clear any previous responses for this attempt (just in case)
      await supabase
        .from('quiz_responses')
        .delete()
        .eq('quiz_attempt_id', quizAttemptId);
      
      // Prepare batch of responses
      const responses = Object.entries(selectedAnswers).map(([questionId, userAnswer]) => {
        const question = questions.find(q => q.id === questionId);
        const isCorrect = question ? userAnswer === question.correct_answer : false;
        
        return {
          quiz_attempt_id: quizAttemptId,
          question_id: questionId,
          user_answer: userAnswer,
          is_correct: isCorrect
        };
      });
      
      if (responses.length > 0) {
        const { error } = await supabase
          .from('quiz_responses')
          .insert(responses);
        
        if (error) {
          console.error('Error saving responses:', error);
          throw error;
        }
      }
    } catch (error) {
      console.error('Error saving user responses:', error);
      toast({
        title: "Error",
        description: "Failed to save your responses. Your score may not be accurate.",
        variant: "destructive"
      });
      return false;
    }
    
    return true;
  };

  const handleQuizEnd = async () => {
    setIsSubmitting(true);
    
    try {
      const calculatedScore = calculateScore();
      setScore(calculatedScore);
      
      // Check if passed
      const hasPassed = calculatedScore >= PASSING_SCORE;
      setPassed(hasPassed);
      
      const timeSpent = totalTime - timeLeft;
      
      // Save user responses
      const responsesSaved = await saveUserResponses();
      if (!responsesSaved) {
        throw new Error("Failed to save responses");
      }
      
      // Save quiz result
      const { error: resultError } = await supabase
        .from('quiz_results')
        .insert({
          user_id: userId,
          skill: skill,
          score: calculatedScore,
          total_questions: questions.length,
          time_spent: timeSpent,
          passed: hasPassed
        });
      
      if (resultError) throw resultError;
      
      setQuizCompleted(true);
      onComplete();
    } catch (error) {
      console.error('Error saving quiz results:', error);
      toast({
        title: "Error",
        description: "Failed to save quiz results. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitQuiz = () => {
    // Verify we have answers for all questions
    const answeredQuestions = Object.keys(selectedAnswers).length;
    if (answeredQuestions < questions.length) {
      const unanswered = questions.length - answeredQuestions;
      toast({
        title: "Warning",
        description: `You have ${unanswered} unanswered question${unanswered > 1 ? 's' : ''}. Are you sure you want to submit?`,
        variant: "destructive"
      });
    }
    
    handleQuizEnd();
  };

  return {
    questions,
    currentQuestionIndex,
    selectedAnswers,
    timeLeft,
    totalTime,
    isLoading,
    isSubmitting,
    quizCompleted,
    score,
    passed,
    handleAnswerSelect,
    handleNextQuestion,
    handlePrevQuestion,
    handleSubmitQuiz,
    handleQuizEnd
  };
};
